import { useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import { Checklist } from "../../../../../../models/checklist";
import { Error } from "../../../../../../models/error";
import { Searches, Filters, Sorts, Sort } from "../../../../../../models/shipeezi.table";
import ChecklistService from "../../../../../../services/checklist.service";
import { Status } from "../../../../../../util/enums";

const useActions = () => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const [checklist, setChecklist] = useState<Checklist>();
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>({ status: [Status.ACTIVE] });
  const [sorting, setSorting] = useState<Sorts>();

  const { refetch, isFetching } = useQuery(
    ["checklist.list", page, pageSize, filters, searches, sorting],
    () =>
      ChecklistService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy: sorting
          ? JSON.stringify([...sorting, ["updatedAt", "desc"]])
          : JSON.stringify([["updatedAt", "desc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        include: JSON.stringify([
          {
            association: "questions",
            where: { valid: true },
            required: true,
            order: [["id", "ASC"]],
            include: [
              { association: "options", where: { valid: true }, required: false },
              { association: "questionLogics", where: { valid: true }, required: false },
            ],
          },
          { association: "jobsType" },
          { association: "tasksType" },
          { association: "appEvents" },
          { association: "branches" },
        ]),
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setChecklists(response.data);
      },
    }
  );

  const { refetch: refetchChecklistTotal } = useQuery(
    ["checklist.total", filters, searches],
    () =>
      ChecklistService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setTotal(response.data);
        else setTotal(0);
      },
    }
  );

  const { mutate: updateChecklist, isLoading: isUpdating } = useMutation(
    ["checklist.update"],
    ({ id, checklist }: { id: number; checklist: Checklist }) =>
      ChecklistService.update(id, checklist),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchChecklistTotal();
      },
      onError: (error: AxiosError<Error>) => {
        setHasErrors(true);
        if (error?.response?.data?.message) NotificationToast.error(error?.response?.data?.message);
      },
    }
  );

  const { mutate: createChecklist, isLoading: isCreating } = useMutation(
    ["checklist.create"],
    (checklist: Checklist) => ChecklistService.create(checklist),
    {
      onSuccess: () => {
        setHasErrors(false);
        refetch();
        refetchChecklistTotal();
      },
      onError: (error: AxiosError<Error>) => {
        setHasErrors(true);
        let errorMessage = error?.response?.data?.message;

        if (error?.response?.data?.message === "Validation error")
          errorMessage = translate("valueOfEntryIsAlreadyInUsed").replace("entry", "code");
        if (errorMessage) NotificationToast.error(errorMessage);
      },
    }
  );

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleGetDefaultFilters = () => Object.values(filters)?.[0];

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  return {
    checklist,
    checklists,
    hasErrors,
    total,
    page,
    pageSize,
    isLoading: isFetching || isCreating || isUpdating,
    setChecklist,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleGetDefaultFilters,
    handleSort,
    updateChecklist,
    createChecklist,
  };
};

export default useActions;
